import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { EmailField, PhoneField, TextField } from '../../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../../ui/forms';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Select,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { StaffRole } from '@velora/contracts';
import { StepFooter } from '../StepFooter';
import { productApi } from '../../../api';

/**
 * Setup step 3 — Staff and roles. Real add-staff form + list. The permission
 * matrix + invitation SMS ship with the Roles module (Admin #28); for now
 * `role` is a label and staff show as "invited" once added.
 */
const ROLE_LABELS: Record<StaffRole, string> = {
  principal: 'Principal',
  vice_principal: 'Vice principal',
  coordinator: 'Coordinator',
  teacher: 'Teacher',
  clerk: 'Clerk',
  support: 'Support',
};

export function StaffSetupPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['staff', institution],
    queryFn: () => productApi.listStaff(institution),
    enabled: !!institution,
  });
  const del = useMutation({
    mutationFn: (id: string) => productApi.deleteStaff(institution, id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['staff', institution] }); void message.success('Marked inactive'); },
  });

  const total = list.data?.total ?? 0;
  const canMarkDone = total > 0;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 14 }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Staff and roles
        </Typography.Title>
        <Typography.Text type="secondary">
          Invite teachers last — they'll need the timetable. Every role is a permission-key set
          (P-01) once the Roles module ships; today `role` is a label.
        </Typography.Text>
      </div>

      <Card
        size="small"
        title={`Staff (${total})`}
        extra={
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Add staff
          </Button>
        }
      >
        {total === 0 ? (
          <Empty
            description={
              <span>
                No staff yet. <a onClick={() => setAddOpen(true)}>Add the first person</a>.
              </span>
            }
            style={{ padding: 16 }}
          />
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={list.data?.rows ?? []}
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'name' },
              {
                title: 'Role',
                dataIndex: 'role',
                key: 'role',
                render: (v: StaffRole) => <Tag>{ROLE_LABELS[v]}</Tag>,
              },
              { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Email', dataIndex: 'email', key: 'email', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (v: string) => <Tag color={v === 'active' ? 'success' : 'default'}>{v}</Tag>,
              },
              {
                key: 'action', width: 40, align: 'right',
                render: (_, r) => (
                  r.status === 'active' ? (
                    <Popconfirm title="Mark inactive?" description="D-06: we deactivate, never delete." onConfirm={() => del.mutate(r.id)} okText="Mark inactive" okButtonProps={{ danger: true }}>
                      <Button size="small" type="text" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                  ) : null
                ),
              },
            ]}
          />
        )}
      </Card>

      {!canMarkDone && (
        <Alert type="warning" showIcon style={{ marginTop: 12 }} message="Add at least one staff member before marking done." />
      )}

      <StepFooter
        institution={institution}
        step="staff"
        summary={`${total} staff on file`}
        disabled={!canMarkDone}
        disabledReason="Add at least one staff member first"
        nextHref={`/i/${institution}/setup/fees`}
        nextLabel="Next: fees →"
      />

      <AddStaffModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          void qc.invalidateQueries({ queryKey: ['staff', institution] });
          setAddOpen(false);
          void message.success('Staff added');
        }}
      />
    </div>
  );
}

type StaffFormValues = { name: string; email?: string; phone: string; role: StaffRole };

function AddStaffModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  return (
    <FormModal<StaffFormValues>
      open={open} onClose={onClose}
      title="Add staff" submitText="Add"
      initial={{ role: 'teacher' }}
      mutation={(v) => productApi.createStaff(institution, {
        name: v.name, email: v.email || undefined, phone: v.phone, role: v.role,
      })}
      onSuccess={onCreated}
    >
      <Field name="name" label="Full name" required>
        <TextField placeholder="A. Sharma" autoFocus />
      </Field>
      <FormSection cols={2}>
        <Field name="phone" label="Phone" required rules={[V.phone()]}>
          <PhoneField />
        </Field>
        <Field name="email" label="Email" optional rules={[V.email()]}>
          <EmailField placeholder="teacher@school.in" />
        </Field>
      </FormSection>
      <Field name="role" label="Role" required>
        <Select
          options={(Object.keys(ROLE_LABELS) as StaffRole[]).map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
        />
      </Field>
    </FormModal>
  );
}
